
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HotelLayout from '@/components/Hotels/HotelLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Star, MapPin, Wifi, Coffee, DollarSign, Check } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { searchHotels, MOCK_HOTELS, getHotelPrices } from '@/utils/hotels/mockData';

const HotelSearch: React.FC = () => {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const [priceRange, setPriceRange] = useState<[number, number]>([50, 500]);
  const [view, setView] = useState<'list' | 'grid'>('list');
  
  // For this demo, we're using the mock data directly
  const hotels = MOCK_HOTELS;
  
  const handleHotelClick = (hotelId: string) => {
    navigate(`/hotels/${hotelId}`);
  };
  
  const getBestPrice = (hotelId: string): number => {
    const prices = getHotelPrices(hotelId);
    if (prices.length === 0) return 0;
    return Math.min(...prices.map(price => price.price));
  };
  
  return (
    <HotelLayout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters sidebar */}
          <div className="lg:w-1/4 space-y-6">
            <Card>
              <CardContent className="p-4 space-y-4">
                <h2 className="text-lg font-semibold">Search</h2>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Destination</label>
                    <Input type="text" placeholder="Where are you going?" defaultValue="New York" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Check-in / Check-out</label>
                    <Input type="text" placeholder="Add dates" defaultValue="Jul 14 - Jul 21" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Guests</label>
                    <Input type="text" placeholder="Add guests" defaultValue="2 adults, 1 room" />
                  </div>
                  <Button className="w-full">Search</Button>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 space-y-4">
                <h2 className="text-lg font-semibold">Filter by:</h2>
                
                <div className="space-y-2">
                  <h3 className="font-medium text-sm">Price per night</h3>
                  <div className="px-2">
                    <Slider
                      defaultValue={[priceRange[0], priceRange[1]]}
                      max={1000}
                      step={10}
                      onValueChange={(values) => setPriceRange([values[0], values[1]])}
                    />
                    <div className="flex justify-between mt-2">
                      <span className="text-sm">${priceRange[0]}</span>
                      <span className="text-sm">${priceRange[1]}+</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="font-medium text-sm">Star Rating</h3>
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map(rating => (
                      <div className="flex items-center space-x-2" key={rating}>
                        <Checkbox id={`star-${rating}`} />
                        <label htmlFor={`star-${rating}`} className="text-sm flex items-center">
                          {Array(rating).fill(0).map((_, i) => (
                            <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />
                          ))}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="font-medium text-sm">Amenities</h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="wifi" />
                      <label htmlFor="wifi" className="text-sm flex items-center">
                        <Wifi size={14} className="mr-1" /> Free WiFi
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="breakfast" />
                      <label htmlFor="breakfast" className="text-sm flex items-center">
                        <Coffee size={14} className="mr-1" /> Breakfast included
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="pool" />
                      <label htmlFor="pool" className="text-sm">Pool</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="parking" />
                      <label htmlFor="parking" className="text-sm">Free parking</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="pet-friendly" />
                      <label htmlFor="pet-friendly" className="text-sm">Pet friendly</label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Hotel listings */}
          <div className="lg:w-3/4">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-xl font-bold">Hotels in New York</h1>
              <Tabs defaultValue="list" className="w-auto" onValueChange={(value) => setView(value as 'list' | 'grid')}>
                <TabsList className="grid w-[120px] grid-cols-2">
                  <TabsTrigger value="list">List</TabsTrigger>
                  <TabsTrigger value="grid">Grid</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            <div className="space-y-4">
              {/* List View */}
              {view === 'list' && hotels.map(hotel => (
                <Card key={hotel.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleHotelClick(hotel.id)}>
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/3 h-[200px] md:h-auto relative">
                      <img 
                        src={hotel.thumbnail} 
                        alt={hotel.name} 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                    <div className="md:w-2/3 p-4 flex flex-col">
                      <div className="flex justify-between">
                        <h2 className="text-xl font-semibold mb-2">{hotel.name}</h2>
                        <div className="flex">
                          {Array(hotel.stars).fill(0).map((_, i) => (
                            <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground mb-2">
                        <MapPin size={14} className="mr-1" />
                        <span>{hotel.location}, {hotel.city}</span>
                      </div>
                      <p className="text-sm mb-2 line-clamp-2">{hotel.description}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {hotel.amenities.slice(0, 3).map((amenity, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-muted rounded-full flex items-center">
                            <Check size={12} className="mr-1" /> {amenity}
                          </span>
                        ))}
                        {hotel.amenities.length > 3 && (
                          <span className="text-xs px-2 py-0.5 bg-muted rounded-full">
                            +{hotel.amenities.length - 3} more
                          </span>
                        )}
                      </div>
                      <div className="mt-auto flex justify-between items-end">
                        <div className="flex items-center">
                          <div className="bg-primary/10 text-primary font-semibold rounded-full px-2 py-0.5 text-sm">
                            {hotel.rating}
                          </div>
                          <span className="ml-2 text-sm text-muted-foreground">
                            ({hotel.reviewCount} reviews)
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Price per night</p>
                          <p className="text-2xl font-bold font-chroma">${getBestPrice(hotel.id)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
              
              {/* Grid View */}
              {view === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {hotels.map(hotel => (
                    <Card key={hotel.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleHotelClick(hotel.id)}>
                      <div className="h-[160px] relative">
                        <img 
                          src={hotel.thumbnail} 
                          alt={hotel.name} 
                          className="w-full h-full object-cover" 
                        />
                      </div>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold line-clamp-1">{hotel.name}</h3>
                          <div className="flex">
                            {Array(hotel.stars).fill(0).map((_, i) => (
                              <Star key={i} size={12} className="fill-yellow-400 text-yellow-400" />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center text-xs text-muted-foreground mb-2">
                          <MapPin size={12} className="mr-1" />
                          <span className="truncate">{hotel.location}, {hotel.city}</span>
                        </div>
                        <div className="flex justify-between items-end mt-4">
                          <div className="bg-primary/10 text-primary font-semibold rounded-full px-2 py-0.5 text-xs">
                            {hotel.rating}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">From</p>
                            <p className="text-lg font-bold font-chroma">${getBestPrice(hotel.id)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </HotelLayout>
  );
};

export default HotelSearch;
